// src/utils/printInvoice.js
// Opens a clean, print-ready invoice in a new window (not the admin sidecart).

const SETTINGS_URL = 'https://demo-site.creativepulse.pk/demo-api/public/get-settings.php';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (n) =>
  'Rs. ' + Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 });

const paymentLabel = (m) =>
  m === 'cod' ? 'Cash On Delivery' : m === 'bank_transfer' ? 'Bank Transfer' : (m || '—');

const formatDate = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export async function printInvoice(order) {
  if (!order) return;

  // Open the window synchronously (inside the click) so popup blockers allow it.
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) {
    alert('Please allow pop-ups for this site to print the invoice.');
    return;
  }
  win.document.write('<p style="font-family:sans-serif;padding:24px">Preparing invoice…</p>');

  // Store details (name, logo, contact) from settings
  let s = {};
  try {
    const res = await fetch(SETTINGS_URL);
    const data = await res.json();
    s = data.settings || data || {};
  } catch (e) {
    s = {};
  }

  const storeName = s.store_name || 'UrbanThread';
  const logo = s.logo || '';
  const storeContact = [s.store_phone, s.store_email].filter(Boolean).join('  |  ');
  const storeAddress = [s.store_address, s.store_city].filter(Boolean).join(', ');

  // Items
  let items = order.items;
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch (e) { items = []; }
  }
  items = Array.isArray(items) ? items : [];

  const rows = items.map((it, i) => {
    const qty = Number(it.quantity ?? it.qty ?? 1);
    const price = Number(it.price ?? 0);
    const size = it.size || it.selectedSize || '';
    return `
      <tr>
        <td class="c">${i + 1}</td>
        <td>
          <div class="item-name">${esc(it.name || it.title || 'Product')}</div>
          ${size ? `<div class="item-meta">Size: ${esc(size)}</div>` : ''}
        </td>
        <td class="r">${money(price)}</td>
        <td class="c">${qty}</td>
        <td class="r">${money(price * qty)}</td>
      </tr>`;
  }).join('');

  // Totals (fall back to computing from items for older orders)
  const computedSubtotal = items.reduce(
    (sum, it) => sum + Number(it.price ?? 0) * Number(it.quantity ?? it.qty ?? 1), 0);
  const subtotal = Number(order.subtotal_amount) > 0 ? Number(order.subtotal_amount) : computedSubtotal;
  const shipping = Number(order.shipping_amount) || 0;
  const promo = Number(order.promo_discount_amount) || 0;
  const flat = Number(order.flat_discount_amount) || 0;
  const total = Number(order.total_amount) || (subtotal + shipping - promo - flat);

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Invoice ${esc(order.order_number)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; background: #fff; padding: 40px; font-size: 13px; line-height: 1.5; }
  .invoice { max-width: 780px; margin: 0 auto; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 3px solid #1a1a1a; }
  .brand img { max-height: 56px; max-width: 200px; display: block; margin-bottom: 8px; }
  .brand h1 { font-size: 22px; letter-spacing: 1px; }
  .brand p { color: #666; font-size: 12px; }
  .title { text-align: right; }
  .title h2 { font-size: 30px; letter-spacing: 3px; font-weight: 300; }
  .title p { margin-top: 6px; color: #444; }
  .title strong { color: #000; }
  .info { display: flex; gap: 40px; margin: 28px 0; }
  .info > div { flex: 1; }
  .info h4 { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #888; margin-bottom: 8px; }
  .info p { margin-bottom: 2px; }
  .name { font-weight: 600; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  thead th { background: #1a1a1a; color: #fff; padding: 10px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; text-align: left; }
  tbody td { padding: 12px; border-bottom: 1px solid #e5e5e5; vertical-align: top; }
  .c { text-align: center; }
  .r { text-align: right; }
  thead th.c { text-align: center; }
  thead th.r { text-align: right; }
  .item-name { font-weight: 600; }
  .item-meta { font-size: 12px; color: #777; margin-top: 2px; }
  .totals { width: 300px; margin: 20px 0 0 auto; }
  .totals .line { display: flex; justify-content: space-between; padding: 6px 0; color: #444; }
  .totals .discount { color: #b3261e; }
  .totals .grand { border-top: 2px solid #1a1a1a; margin-top: 6px; padding-top: 10px; font-size: 16px; font-weight: 700; color: #000; }
  .pay { margin-top: 28px; padding: 14px 16px; background: #f6f6f4; border-radius: 6px; display: flex; justify-content: space-between; }
  .foot { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e5e5; text-align: center; color: #777; font-size: 12px; }
  .foot strong { color: #1a1a1a; display: block; font-size: 14px; margin-bottom: 4px; }
  @media print {
    body { padding: 0; }
    @page { margin: 14mm; }
    thead th, .pay { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="invoice">
    <div class="top">
      <div class="brand">
        ${logo ? `<img id="inv-logo" src="${esc(logo)}" alt="${esc(storeName)}" />` : `<h1>${esc(storeName)}</h1>`}
        ${storeAddress ? `<p>${esc(storeAddress)}</p>` : ''}
        ${storeContact ? `<p>${esc(storeContact)}</p>` : ''}
      </div>
      <div class="title">
        <h2>INVOICE</h2>
        <p>Invoice #: <strong>${esc(order.order_number)}</strong></p>
        <p>Date: <strong>${formatDate(order.created_at)}</strong></p>
      </div>
    </div>

    <div class="info">
      <div>
        <h4>Billed To</h4>
        <p class="name">${esc(order.customer_name)}</p>
        ${order.customer_phone ? `<p>${esc(order.customer_phone)}</p>` : ''}
        ${order.customer_email ? `<p>${esc(order.customer_email)}</p>` : ''}
      </div>
      <div>
        <h4>Ship To</h4>
        <p>${esc(order.address)}</p>
        <p>${esc(order.city)}</p>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="c" style="width:40px">#</th>
          <th>Item</th>
          <th class="r">Unit Price</th>
          <th class="c">Qty</th>
          <th class="r">Amount</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="5" class="c">No items</td></tr>'}</tbody>
    </table>

    <div class="totals">
      <div class="line"><span>Subtotal</span><span>${money(subtotal)}</span></div>
      <div class="line"><span>Shipping</span><span>${shipping > 0 ? money(shipping) : 'Free'}</span></div>
      ${flat > 0 ? `<div class="line discount"><span>Discount</span><span>- ${money(flat)}</span></div>` : ''}
      ${promo > 0 ? `<div class="line discount"><span>Promo Code</span><span>- ${money(promo)}</span></div>` : ''}
      <div class="line grand"><span>Total</span><span>${money(total)}</span></div>
    </div>

    <div class="pay">
      <span>Payment Method</span>
      <strong>${esc(paymentLabel(order.payment_method))}</strong>
    </div>

    <div class="foot">
      <strong>Thank you for shopping with ${esc(storeName)}!</strong>
      This is a computer-generated invoice and does not require a signature.
    </div>
  </div>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();

  const doPrint = () => { win.focus(); win.print(); };
  const img = win.document.getElementById('inv-logo');
  if (img && !img.complete) {
    img.onload = doPrint;
    img.onerror = doPrint;
  } else {
    setTimeout(doPrint, 300);
  }
}

export default printInvoice;
